import React, { useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * HomeGallery — dynamic floating gallery section.
 * Layout adapts to the number of items:
 *   1   item  -> full-width hero
 *   2   items -> side-by-side
 *   3   items -> hero left + 2 stacked right
 *   4   items -> 2x2 with offset
 *   5–6 items -> 3-col asymmetric collage
 *   7+  items -> 4-col flowing collage with varied row spans
 * Each tile gets a subtle floating animation and lightbox-on-click.
 */
const HomeGallery = ({ items = [] }) => {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const containerRef = useRef(null);

  // Subtle floating animation: each tile gets a unique delay
  useEffect(() => {
    if (!containerRef.current) return;
    const tiles = containerRef.current.querySelectorAll("[data-float]");
    tiles.forEach((t, i) => {
      t.style.animationDelay = `${(i % 6) * 0.4}s`;
    });
  }, [items.length]);

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIdx(null);
      if (e.key === "ArrowRight") setLightboxIdx((i) => (i + 1) % items.length);
      if (e.key === "ArrowLeft") setLightboxIdx((i) => (i - 1 + items.length) % items.length);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxIdx, items.length]);

  if (!items || items.length === 0) return null;

  // ── Build a layout descriptor based on count ─────────────────────
  // Returns an array of { item, span: { col, row }, height }
  const buildLayout = () => {
    const n = items.length;
    if (n === 1) {
      return [{ item: items[0], col: "span 12", row: "span 2", height: "500px" }];
    }
    if (n === 2) {
      return items.map((it) => ({ item: it, col: "span 6", row: "span 2", height: "460px" }));
    }
    if (n === 3) {
      return [
        { item: items[0], col: "span 8", row: "span 2", height: "460px" },
        { item: items[1], col: "span 4", row: "span 1", height: "220px" },
        { item: items[2], col: "span 4", row: "span 1", height: "220px" },
      ];
    }
    if (n === 4) {
      return [
        { item: items[0], col: "span 7", row: "span 2", height: "460px" },
        { item: items[1], col: "span 5", row: "span 1", height: "220px" },
        { item: items[2], col: "span 3", row: "span 1", height: "220px" },
        { item: items[3], col: "span 2", row: "span 1", height: "220px" },
      ];
    }
    if (n <= 6) {
      // 5-6 items: cinematic asymmetric — first is hero, others varied
      const layouts = [
        { col: "span 6", row: "span 2", height: "460px" },
        { col: "span 6", row: "span 1", height: "220px" },
        { col: "span 3", row: "span 1", height: "220px" },
        { col: "span 3", row: "span 1", height: "220px" },
        { col: "span 4", row: "span 1", height: "220px" },
        { col: "span 8", row: "span 1", height: "220px" },
      ];
      return items.map((it, i) => ({ item: it, ...(layouts[i] || layouts[0]) }));
    }
    // 7+ items: flowing masonry-style with varied widths/heights cycling through a pattern
    const patterns = [
      { col: "span 6", row: "span 2", height: "460px" },
      { col: "span 3", row: "span 1", height: "220px" },
      { col: "span 3", row: "span 1", height: "220px" },
      { col: "span 6", row: "span 1", height: "220px" },
      { col: "span 4", row: "span 2", height: "460px" },
      { col: "span 4", row: "span 1", height: "220px" },
      { col: "span 4", row: "span 1", height: "220px" },
      { col: "span 8", row: "span 1", height: "220px" },
      { col: "span 4", row: "span 1", height: "220px" },
      { col: "span 3", row: "span 1", height: "220px" },
      { col: "span 3", row: "span 1", height: "220px" },
      { col: "span 6", row: "span 1", height: "220px" },
    ];
    return items.map((it, i) => ({ item: it, ...patterns[i % patterns.length] }));
  };

  const tiles = buildLayout();

  return (
    <section className="py-24 border-t border-gold/10 relative overflow-hidden" data-testid="home-gallery">
      {/* Subtle background sparkle */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(184,115,51,0.08), transparent 60%)" }}
      />

      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-8 bg-gold/60" />
            <span className="font-body text-gold text-[0.65rem] font-semibold tracking-[0.4em] uppercase">Gallery</span>
            <div className="h-px w-8 bg-gold/60" />
          </div>
          <h2 className="font-display font-light text-4xl md:text-5xl text-white">
            Moments in <span className="gold-gradient-text italic">Motion</span>
          </h2>
          <p className="font-body text-white/40 text-sm mt-4 max-w-md mx-auto">
            A living archive of the stages, the spotlights, and the stories we’ve been part of.
          </p>
        </div>

        <div
          ref={containerRef}
          className="grid grid-cols-12 gap-3 auto-rows-min"
        >
          {tiles.map(({ item, col, row, height }, idx) => (
            <div
              key={item.id}
              data-float
              onClick={() => setLightboxIdx(idx)}
              className="hg-tile relative overflow-hidden rounded-sm group cursor-pointer"
              style={{
                gridColumn: col,
                gridRow: row,
                minHeight: height,
                animation: "floatY 6s ease-in-out infinite",
              }}
              data-testid={`home-gallery-tile-${item.id}`}
            >
              <img
                src={item.image}
                alt={item.caption || "MRVA stage moment"}
                className="w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-110"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              {/* Vignette */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/10" />
              {/* Caption */}
              {item.caption && (
                <div className="absolute inset-x-0 bottom-0 p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  <div className="flex items-center gap-2">
                    <div className="h-px w-5 bg-gold" />
                    <span className="font-body text-gold text-[0.55rem] tracking-[0.3em] uppercase font-semibold">
                      {item.caption}
                    </span>
                  </div>
                </div>
              )}
              {/* Copper sheen on hover */}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                style={{ background: "radial-gradient(circle at 50% 100%, rgba(184,115,51,0.18), transparent 60%)" }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && items[lightboxIdx] && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center p-6 animate-fade-in-up"
          style={{ animationDuration: "0.3s" }}
          data-testid="home-gallery-lightbox"
        >
          <div className="absolute inset-0 bg-black/95" onClick={() => setLightboxIdx(null)} />
          <button
            onClick={() => setLightboxIdx(null)}
            className="absolute top-6 right-6 w-11 h-11 rounded-sm border border-gold/40 bg-black/60 text-gold hover:bg-gold/20 transition-all flex items-center justify-center z-10"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          {items.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((lightboxIdx - 1 + items.length) % items.length); }}
                className="absolute left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-sm border border-gold/40 bg-black/60 text-gold hover:bg-gold/20 transition-all flex items-center justify-center z-10"
                aria-label="Previous"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((lightboxIdx + 1) % items.length); }}
                className="absolute right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-sm border border-gold/40 bg-black/60 text-gold hover:bg-gold/20 transition-all flex items-center justify-center z-10"
                aria-label="Next"
              >
                <ChevronRight size={20} />
              </button>
            </>
          )}
          <div className="relative max-w-6xl max-h-[90vh] z-[1]" onClick={(e) => e.stopPropagation()}>
            <img
              src={items[lightboxIdx].image}
              alt={items[lightboxIdx].caption || "Gallery"}
              className="max-h-[90vh] max-w-full w-auto h-auto object-contain rounded-sm"
            />
            {items[lightboxIdx].caption && (
              <div className="absolute bottom-0 left-0 right-0 px-6 py-4 bg-gradient-to-t from-black/90 to-transparent">
                <p className="font-display text-white text-lg">{items[lightboxIdx].caption}</p>
              </div>
            )}
            <div className="absolute top-3 left-3 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-sm border border-gold/30">
              <span className="font-body text-gold text-[0.6rem] tracking-[0.3em] uppercase">
                {lightboxIdx + 1} / {items.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default HomeGallery;
